import type { ApexOptions } from 'apexcharts';

import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';

import { useSettingsStore } from '@/store/useSettingsStore';

const CHART_FONT_FAMILY = 'Aptos, sans-serif';

const SERIES_CONFIG = [
  { name: 'School', color: '#F59E0B' },
  { name: 'Parents', color: '#EC4899' },
  { name: 'Students', color: '#9333EA' },
];

interface IUserGrowthTrendsData {
  label: string;
  school: number;
  parents: number;
  students: number;
}

interface IUserGrowthTrendsChartProps {
  data: IUserGrowthTrendsData[];
}

const UserGrowthTrendsChart: React.FC<IUserGrowthTrendsChartProps> = ({
  data,
}) => {
  const direction = useSettingsStore((s) => s.settings.direction);
  const isRTL = direction === 'rtl';

  const series = useMemo(
    () => [
      { name: 'School', data: data.map((d) => d.school) },
      { name: 'Parents', data: data.map((d) => d.parents) },
      { name: 'Students', data: data.map((d) => d.students) },
    ],
    [data]
  );

  const yAxisMax = useMemo(() => {
    const allValues =
      data?.flatMap((d) => [
        d?.school ?? 0,
        d?.parents ?? 0,
        d?.students ?? 0,
      ]) || [];
    let max = 0;
    if (allValues.length > 0) {
      max = Math.max(...allValues, 0);
    }
    if (max === 0 || isNaN(max)) return 10;

    const padded = Math.ceil(max * 1.2);
    const magnitude = Math.pow(10, Math.floor(Math.log10(padded)));
    const step = magnitude >= 10 ? magnitude / 2 : magnitude;
    return Math.ceil(padded / step) * step;
  }, [data]);

  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'bar',
        height: 280,
        toolbar: { show: false },
        fontFamily: CHART_FONT_FAMILY,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '60%',
          borderRadius: 4,
          borderRadiusApplication: 'end',
        },
      },
      dataLabels: { enabled: false },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent'],
      },
      colors: SERIES_CONFIG.map((s) => s.color),
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'right',
        fontSize: '12px',
        fontFamily: CHART_FONT_FAMILY,
        markers: {
          size: 6,
          shape: 'square',
          offsetX: -4,
        },
        itemMargin: { horizontal: 12, vertical: 0 },
        labels: { colors: '#525866' },
      },
      xaxis: {
        categories: data.map((d) => d.label),
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
          style: {
            colors: '#525866',
            fontSize: '12px',
            fontWeight: 400,
            fontFamily: CHART_FONT_FAMILY,
          },
        },
      },
      yaxis: {
        min: 0,
        max: yAxisMax,
        tickAmount: 5,
        opposite: isRTL,
        labels: {
          style: {
            colors: '#525866',
            fontSize: '12px',
            fontWeight: 400,
            fontFamily: CHART_FONT_FAMILY,
          },
          formatter: (value: number) => {
            if (value === 0) return '0';
            return `${value}`;
          },
        },
      },
      grid: {
        borderColor: '#E1E4EA',
        strokeDashArray: 0,
        position: 'back',
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
        padding: { top: 0, right: 0, bottom: 0, left: 0 },
      },
      tooltip: {
        enabled: true,
        shared: true,
        intersect: false,
        theme: 'light',
        style: { fontSize: '12px', fontFamily: CHART_FONT_FAMILY },
        y: {
          formatter: (val: number) => `${val.toLocaleString()}`,
        },
      },
      states: {
        hover: { filter: { type: 'darken', value: 0.85 } },
        active: { filter: { type: 'darken', value: 0.85 } },
      },
    }),
    [data, isRTL, yAxisMax]
  );

  return (
    <div className="w-full">
      <ReactApexChart
        options={options}
        series={series}
        type="bar"
        height={280}
      />
    </div>
  );
};

export default UserGrowthTrendsChart;
